import React, { useEffect, useState } from 'react';
import {
    Form,
    Input,
    InputNumber,
    Switch,
    Select,
    Divider,
    Row,
    Col,
    message,
    Button,
    DatePicker,
    Upload,
    FormInstance,


} from 'antd';
import { WCCategory, WCProduct } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import dayjs from 'dayjs';
import { decodeHtmlEntities } from '../../utils/helpers';
import { PlusOutlined } from '@ant-design/icons';
import { RcFile, UploadFile } from 'antd/es/upload';

interface ProductFormProps {
    form: FormInstance;
    product: WCProduct | null;
    onCancel: () => void;
    onSubmit: (values: Partial<WCProduct>) => void;
}


const ProductForm: React.FC<ProductFormProps> = ({ form, product, onSubmit, onCancel }) => {
    const [categories, setCategories] = useState<WCCategory[]>([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await WordPressAPI.getCategories();
                setCategories(data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    const [fileList, setFileList] = useState<UploadFile[]>(
        product?.images?.map(img => ({
            uid: String(img.id),
            name: img.name,
            status: 'done',
            url: img.src,
        })) || []
    );




    useEffect(() => {
        const values = {
            ...product,
            categories: Array.isArray(product?.categories)
                ? product.categories.map((cat: { id: number }) => cat.id)
                : [],
            dimensions: {
                length: product?.dimensions?.length ? Number(product.dimensions.length) : '',
                width: product?.dimensions?.width ? Number(product.dimensions.width) : '',
                height: product?.dimensions?.height ? Number(product.dimensions.height) : ''
            },
            weight: product?.weight ? Number(product.weight) : '',
            can_be_rotated: Boolean(product?.can_be_rotated),
            can_fill_pallet_gaps: Boolean(product?.can_fill_pallet_gaps),
            regular_price: product?.regular_price ? product.regular_price.toString() : '',
            has_sale: Boolean(product?.sale_price),
            sale_price: product?.sale_price ? product.sale_price.toString() : undefined,
            date_on_sale_from: product?.date_on_sale_from ? dayjs(product.date_on_sale_from) : null,
            date_on_sale_to: product?.date_on_sale_to ? dayjs(product.date_on_sale_to) : null,
            images: fileList,
        };
        form.setFieldsValue(values);
    }, [product, form, fileList]);

    const handleSubmit = (values: any) => {
        const formattedValues = {
            ...values,
            regular_price: values.regular_price?.toString(),
            sale_price: values.has_sale ? values.sale_price?.toString() : undefined,
            date_on_sale_from: values.has_sale ? values.date_on_sale_from?.toISOString() : '',
            date_on_sale_to: values.has_sale ? values.date_on_sale_to?.toISOString() : '',
            categories: Array.isArray(values?.categories)
                ? values.categories.map((categoryId: number) =>
                    categories.find(cat => cat.id === categoryId) || { id: categoryId }
                )
                : [],
            images: values.images?.filter((img: UploadFile) => img.uid)
                .map((img: UploadFile) => ({
                    id: Number(img.uid),
                    src: img.url,
                    name: img.name
                })),
            meta_data: [
                ...Object.entries(values)
                    .filter(([key]) => !['id', 'date_created', 'date_modified', 'name', 'price', 'regular_price', 'sale_price', 'sku', 'stock_status', 'stock_quantity', 'categories', 'dimensions', 'weight'].includes(key))
                    .map(([key, value]) => ({
                        key,
                        value: value === undefined ? '' : value
                    }))
            ]
        };
        onSubmit(formattedValues);
    };

    const beforeUpload = async (file: RcFile, fileList: RcFile[], info: { file: RcFile, fileList: RcFile[] }, type: 'FeaturedImage' | 'ProductGallery'): Promise<boolean> => {
        try {
            const response = await WordPressAPI.uploadMedia(file);
            if (response) {
                const newImage: UploadFile = {
                    uid: String(response.id),
                    name: response.title.rendered,
                    status: 'done' as const,
                    url: response.source_url,
                };

                if (type === 'FeaturedImage') {
                    form.setFieldsValue({
                        images: [newImage, ...fileList]
                    });
                    setFileList(prev => [newImage, ...prev]);
                }
                else {
                    form.setFieldsValue({
                        images: [...fileList, newImage]
                    });
                    setFileList(prev => [...prev, newImage]);
                }

            }
            return false;
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload file');
            return false;
        }
    };

    const handleRemove = (file: UploadFile, type: 'FeaturedImage' | 'ProductGallery') => {
        const newFileList = fileList.filter(item => item.uid !== file.uid);
        setFileList(newFileList);

        if (type === 'FeaturedImage') {
            form.setFieldsValue({
                images: newFileList
            });
        } else {
            form.setFieldsValue({
                images: newFileList
            });
        }

        return true;
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            preserve={false}
        >
            <Divider>Basic Information</Divider>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="thumbnail_id" label="Featured Image">
                        <Upload
                            listType="picture-card"
                            maxCount={1}
                            className="dark-remove-icon"
                            beforeUpload={(file, fileList) => beforeUpload(file, fileList, { file, fileList }, "FeaturedImage")}
                            fileList={fileList.length > 0 ? [{
                                uid: String(fileList[0].uid),
                                name: fileList[0].name,
                                status: 'done',
                                url: fileList[0].url,
                            }] : []}
                            onRemove={(file) => handleRemove(file, 'FeaturedImage')}
                        >
                            {fileList.length === 0 && <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>}
                        </Upload>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter product name' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="barcode" label="Barcode">
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="regular_price"
                        label="Regular Price"
                        rules={[{ required: true, message: 'Please enter regular price' }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{ width: '100%' }}
                            addonBefore="$"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="last_price"
                        label="Last Price"
                    >
                        <div className="text-gray-500">
                            ${Number(product?.last_price || 0).toFixed(2)}
                        </div>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item name="has_sale" valuePropName="checked">
                <Switch
                    checkedChildren="Sale Price Enabled"
                    unCheckedChildren={<span className="text-gray-700">Sale Price Disabled</span>}
                    className="bg-gray-300 ant-switch-checked:bg-blue-500"
                />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.has_sale !== curr.has_sale}>
                {({ getFieldValue }) =>
                    getFieldValue('has_sale') ? (
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="sale_price"
                                    label="Sale Price"
                                    rules={[{ required: true, message: 'Please enter sale price' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                        style={{ width: '100%' }}
                                        addonBefore="$"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="date_on_sale_from" label="Sale Start Date">
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        showTime
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="date_on_sale_to" label="Sale End Date">
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        showTime
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    ) : null
                }
            </Form.Item>

            <Divider>Carton Dimensions</Divider>
            <Row gutter={16}>
                <Col span={6}>
                    <Form.Item
                        name={['dimensions', 'length']}
                        label="Length"
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        name={['dimensions', 'width']}
                        label="Width"
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        name={['dimensions', 'height']}
                        label="Height"
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        name="weight"
                        label="Weight"
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>Pallet Settings</Divider>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Package Type" name="unit_packaging">
                        <Select>
                            <Select.Option value="box:Box">Box</Select.Option>
                            <Select.Option value="bag:Bag">Bag</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="max_stack_height" label="Max Stack Height">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="can_be_rotated" label="Can be rotated" valuePropName="checked">
                        <Switch className="bg-gray-300 ant-switch-checked:bg-blue-500" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="can_fill_pallet_gaps" label="Can fill pallet gaps" valuePropName="checked">
                        <Switch className="bg-gray-300 ant-switch-checked:bg-blue-500" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item name="categories" label="Categories">
                        <Select
                            mode="multiple"
                            placeholder="Select categories"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {categories.map(category => (
                                <Select.Option key={category.id} value={category.id}>
                                    {decodeHtmlEntities(category.name)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Divider>Images</Divider>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item name="images" label="Product Gallery">
                        <Upload
                            listType="picture-card"
                            multiple
                            className="dark-remove-icon"
                            beforeUpload={(file, fileList) => beforeUpload(file, fileList, { file, fileList }, "ProductGallery")}
                            fileList={fileList}
                            onRemove={(file) => handleRemove(file, 'ProductGallery')}
                        >
                            {fileList.length < 8 && <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>}
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit">
                        {product ? 'Update' : 'Create'} Product
                    </Button>
                </div>
            </Form.Item>
        </Form>
    );
};

export default ProductForm; 